"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Group = exports.GroupedObject = void 0;
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const faPlusCircle_1 = require("@fortawesome/free-solid-svg-icons/faPlusCircle");
const get_1 = __importDefault(require("lodash/get"));
const sortBy_1 = __importDefault(require("lodash/sortBy"));
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const react_markdown_1 = __importDefault(require("react-markdown"));
const remark_gfm_1 = __importDefault(require("remark-gfm"));
const helpers_1 = require("evolution-common/lib/utils/helpers");
const Conditional_1 = require("../../actions/utils/Conditional");
const DeleteGroupedObjectButton_1 = __importDefault(require("./widgets/DeleteGroupedObjectButton"));
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
const Widget_1 = require("./Widget");
const GroupedObject = (props) => {
    const { t, i18n } = (0, react_i18next_1.useTranslation)(['survey', 'main']);
    const groupedObjectShortname = props.shortname;
    (0, helpers_1.devLog)('%c rendering ' + groupedObjectShortname + ' ' + props.objectId, 'background: rgba(0,255,0,0.1); font-size: 7px;');
    const path = props.path;
    const groupedObjectId = props.objectId;
    const parentObjectIds = props.parentObjectIds;
    parentObjectIds[groupedObjectShortname] = groupedObjectId;
    const widgetsComponents = props.widgetConfig.widgets.map((widgetShortname, idx) => (react_1.default.createElement(Widget_1.InGroupWidget, { key: widgetShortname, currentWidgetShortname: widgetShortname, nextWidgetShortname: props.widgetConfig.widgets[idx + 1], sectionName: props.section, interview: props.interview, errors: props.errors, user: props.user, loadingState: props.loadingState, pathPrefix: path, widgetStatusPath: `groups.${props.shortname}.${groupedObjectId}`, groupedObjectId: groupedObjectId, parentObjectIds: parentObjectIds, startUpdateInterview: props.startUpdateInterview, startAddGroupedObjects: props.startAddGroupedObjects, startRemoveGroupedObjects: props.startRemoveGroupedObjects, startNavigate: props.startNavigate })));
    let title = '';
    const localizedName = props.widgetConfig.name;
    if (typeof localizedName === 'function') {
        title = localizedName(t, (0, get_1.default)(props.interview.response, path), props.sequence, props.interview, path);
    }
    else if (typeof localizedName === 'object') {
        title =
            typeof localizedName[i18n.language] === 'string'
                ? localizedName[i18n.language]
                : localizedName[i18n.language]((0, get_1.default)(props.interview.response, path), props.sequence, props.interview, path);
    }
    else {
        title = localizedName || '';
    }
    return (react_1.default.createElement("div", { className: "survey-group-object" },
        react_1.default.createElement("div", { className: "content-container" },
            react_1.default.createElement("div", { className: "survey-group-object__content" },
                !(0, LodashExtensions_1._isBlank)(title) && (react_1.default.createElement("h3", { className: "survey-group-object__title" },
                    react_1.default.createElement(react_markdown_1.default, { remarkPlugins: [[remark_gfm_1.default, { singleTilde: false }]] }, title))),
                react_1.default.createElement(DeleteGroupedObjectButton_1.default, { interview: props.interview, user: props.user, path: path, widgetConfig: props.widgetConfig, shortname: props.shortname, startRemoveGroupedObjects: props.startRemoveGroupedObjects, startUpdateInterview: props.startUpdateInterview, startAddGroupedObjects: props.startAddGroupedObjects, startNavigate: props.startNavigate }),
                widgetsComponents))));
};
exports.GroupedObject = GroupedObject;
const Group = (props) => {
    const { t, i18n } = (0, react_i18next_1.useTranslation)(['survey']);
    const widgetConfig = props.widgetConfig;
    const onAddGroupedObject = (sequence) => (e) => {
        e.preventDefault();
        props.startAddGroupedObjects(1, sequence, props.path);
    };
    const groupedObjects = () => {
        const { interview, path } = props;
        if (interview && interview.response) {
            let groupedObjects = {};
            const allGroupedObjects = (0, get_1.default)(interview.response, path, {});
            if (typeof widgetConfig.filter === 'function') {
                groupedObjects = widgetConfig.filter(interview, allGroupedObjects);
            }
            else {
                groupedObjects = allGroupedObjects;
            }
            return (0, sortBy_1.default)(Object.values(groupedObjects), ['_sequence']);
        }
        return [];
    };
    if (props.widgetConfig.conditional !== undefined &&
        !(0, Conditional_1.checkConditional)((0, helpers_1.parseBoolean)(props.widgetConfig.conditional, props.interview, props.path), props.interview, props.path, props.user)[0]) {
        return null;
    }
    const widgetsComponents = [];
    const parentObjectIds = (0, cloneDeep_1.default)(props.parentObjectIds) || {};
    // FIXME Type the groupedObject
    groupedObjects().forEach((groupedObject, _index) => {
        // FIXME What are we doing here exactly? Why?
        parentObjectIds[props.shortname] = groupedObject._uuid;
        const path = `${props.path}.${groupedObject._uuid}`;
        for (const parentGroupShortname in parentObjectIds) {
            path.split('{' + parentGroupShortname + '}').join(parentObjectIds[parentGroupShortname]);
        }
        const showThisGroupedObject = (0, helpers_1.parseBoolean)(props.widgetConfig.groupedObjectConditional, props.interview, path, props.user, true);
        if (showThisGroupedObject) {
            widgetsComponents.push(react_1.default.createElement(exports.GroupedObject, { widgetConfig: props.widgetConfig, path: path, loadingState: props.loadingState, shortname: props.shortname, objectId: groupedObject._uuid, parentObjectIds: parentObjectIds, key: groupedObject._uuid, sequence: groupedObject['_sequence'], section: props.section, interview: props.interview, user: props.user, errors: props.errors, startUpdateInterview: props.startUpdateInterview, startAddGroupedObjects: props.startAddGroupedObjects, startRemoveGroupedObjects: props.startRemoveGroupedObjects, startNavigate: props.startNavigate }));
        }
    });
    const showTitle = (0, helpers_1.parseBoolean)(props.widgetConfig.showTitle, props.interview, props.path, props.user);
    const showAddButton = props.loadingState === 0 &&
        (0, helpers_1.parseBoolean)(props.widgetConfig.showGroupedObjectAddButton, props.interview, props.path, props.user);
    const addButtonLabel = (0, helpers_1.translateString)(props.widgetConfig.groupedObjectAddButtonLabel, i18n, props.interview, props.path) ||
        t(`survey:${props.shortname}:addGroupedObject`);
    const addButtonLocation = props.widgetConfig.addButtonLocation || 'bottom';
    const addButtonSize = props.widgetConfig.addButtonSize || 'large';
    return (react_1.default.createElement("section", { className: "survey-group" },
        react_1.default.createElement("div", { className: "content-container" },
            react_1.default.createElement("div", { className: "survey-group__content" },
                showTitle && (react_1.default.createElement("h2", { className: "survey-group__title" }, (0, helpers_1.translateString)(widgetConfig.title, i18n, props.interview, props.path) ||
                    t(`survey:${props.shortname}:title`))),
                showAddButton && (addButtonLocation === 'top' || addButtonLocation === 'both') && (react_1.default.createElement("div", { className: "center" }, showAddButton && (react_1.default.createElement("button", { type: "button", className: `button blue center ${addButtonSize}`, onClick: onAddGroupedObject(1) },
                    react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faPlusCircle_1.faPlusCircle, className: "faIconLeft" }),
                    addButtonLabel)))),
                widgetsComponents,
                showAddButton &&
                    (addButtonLocation === 'bottom' ||
                        (widgetsComponents.length > 0 && addButtonLocation === 'both')) && (react_1.default.createElement("div", { className: "center" }, showAddButton && (react_1.default.createElement("button", { type: "button", className: `button blue center ${addButtonSize}`, onClick: onAddGroupedObject(-1) },
                    react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faPlusCircle_1.faPlusCircle, className: "faIconLeft" }),
                    addButtonLabel))))))));
};
exports.Group = Group;
//# sourceMappingURL=GroupWidgets.js.map